import { FC, useMemo } from 'react';
import { useLanguagePairContext } from 'src/context/languagePair';
import { PageUrlMeta } from 'src/utils/getPageUrlMeta';
import { getSeoLanguageLocale } from 'src/utils/getSeoLanguageLocale';
import { LABEL_MAP } from './sourceLanguages.const';
import * as S from './styles';

type SourceLanguageDropdownOptionsProps = {
  alternateLinks: PageUrlMeta['alternateLinks'];
  className?: string;
  onSelect: () => void;
};

export const SourceLanguageDropdownOptions: FC<SourceLanguageDropdownOptionsProps> = ({
  alternateLinks,
  className,
  onSelect,
}) => {
  const { sourceLanguageLocale } = useLanguagePairContext();

  const options = useMemo(() => {
    const currentLocale = getSeoLanguageLocale(sourceLanguageLocale);

    const rawOptions = alternateLinks
      .map(link => ({ ...link, label: LABEL_MAP[link.hrefLang] }))
      .filter(({ label }) => label)
      .sort((a, b) => a.label.localeCompare(b.label));

    const currentOption = rawOptions.find(({ hrefLang }) => hrefLang === currentLocale);

    const restOptions = rawOptions.filter(({ hrefLang }) => hrefLang !== currentLocale);

    return currentOption ? [currentOption, ...restOptions] : restOptions;
  }, [alternateLinks, sourceLanguageLocale]);

  return (
    <S.DropdownOptions className={className} data-testid="sourceLanguageDropdownOptions">
      {options.map(({ href, hrefLang, label }) => (
        <S.DropdownOption
          data-testid="sourceLanguageDropdownOption"
          href={href}
          key={hrefLang}
          onClick={onSelect}
        >
          {label}
        </S.DropdownOption>
      ))}
    </S.DropdownOptions>
  );
};
